const DEFAULT_DATE_OPTIONS = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const DEFAULT_LOCALE = 'en-AU';
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isCalendarDateString = (value) => CALENDAR_DATE_PATTERN.test(String(value).trim());

export const parseUtcDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const str = String(value).trim();
  if (!str) return null;

  if (/^\d+$/.test(str)) {
    const num = Number(str);
    return new Date(num < 1e12 ? num * 1000 : num);
  }

  if (isCalendarDateString(str)) {
    return new Date(`${str}T12:00:00.000Z`);
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(str)) {
    return new Date(`${str}Z`);
  }

  return new Date(str);
};

export const formatCalendarDate = (
  value,
  locale = DEFAULT_LOCALE,
  options = DEFAULT_DATE_OPTIONS
) => {
  const str = String(value).trim();
  if (!str) return 'N/A';

  if (isCalendarDateString(str)) {
    return new Date(`${str}T12:00:00.000Z`).toLocaleDateString(locale, {
      ...options,
      timeZone: 'UTC',
    });
  }

  const date = parseUtcDate(value);
  if (!date || Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleDateString(locale, options);
};

export const formatCalendarDateForFilename = (value) => {
  const str = String(value).trim();
  if (isCalendarDateString(str)) return str;

  const formatted = formatCalendarDate(value, 'en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return formatted === 'N/A' ? 'receipt' : formatted;
};

export const formatLocalDate = (value, locale = DEFAULT_LOCALE, options = DEFAULT_DATE_OPTIONS) =>
  formatCalendarDate(value, locale, options);

export const addYearsUtc = (value, years = 1) => {
  const str = String(value).trim();

  if (isCalendarDateString(str)) {
    const [year, month, day] = str.split('-').map(Number);
    return new Date(Date.UTC(year + years, month - 1, day, 12, 0, 0));
  }

  const date = parseUtcDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;

  const result = new Date(date);
  result.setUTCFullYear(result.getUTCFullYear() + years);
  return result;
};

export const formatCalendarExpiryDate = (value, years = 1) => {
  const expiryDate = addYearsUtc(value, years);
  if (!expiryDate) return 'N/A';

  return expiryDate.toLocaleDateString(DEFAULT_LOCALE, {
    ...DEFAULT_DATE_OPTIONS,
    timeZone: 'UTC',
  });
};

export const formatLocalExpiryDate = (value, years = 1) =>
  formatCalendarExpiryDate(value, years);

export const isPastUtcExpiry = (value, years = 1) => {
  const expiryDate = addYearsUtc(value, years);
  if (!expiryDate) return false;

  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

  return todayUtc > expiryDate.getTime();
};
